<template>
  <div id="reviewOrganizationPromptModal" class="modal fade" tabindex="-1">
    <div class="modal-dialog modal-lg modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Review Organization Prompt</h5>
          <button
            type="button"
            class="btn-close"
            aria-label="Close"
            :disabled="saving"
            data-bs-dismiss="modal"
          ></button>
        </div>
        <div class="modal-body">
          <div v-if="saving" class="d-flex flex-column align-items-center justify-content-center p-5" style="min-height: 200px;">
            <div class="spinner-border text-primary mb-3" role="status">
              <span class="visually-hidden">Creating...</span>
            </div>
            <div class="text-muted">Creating organization prompt...</div>
          </div>

          <template v-else-if="draft">
            <div class="mb-3">
              <label class="form-label fw-bold text-muted small">Customer</label>
              <div>{{ draft.customerName || `Customer ${draft.customerId}` }}</div>
            </div>

            <div class="mb-3">
              <label class="form-label fw-bold text-muted small">Version</label>
              <div v-if="loadingVersion">{{ nextVersion == null ? 'Loading...' : '' }}</div>
              <div v-else>{{ nextVersion != null ? nextVersion : '—' }}</div>
            </div>

            <div class="mb-3">
              <label class="form-label fw-bold text-muted small">
                {{ draft.previousContent != null ? 'Changes' : 'Prompt Content' }}
              </label>
              <pre
                v-if="draft.previousContent != null"
                class="prompt-diff bg-light border rounded p-3 mb-0 small"
                style="max-height: 300px; overflow: auto; white-space: pre-wrap; word-break: break-word;"
              >
                <span
                  v-for="(part, i) in diffSegments"
                  :key="i"
                  :class="{
                    'diff-removed': part.removed,
                    'diff-added': part.added
                  }"
                >{{ part.value }}</span>
              </pre>
              <pre
                v-else
                class="bg-light border rounded p-3 mb-0 small"
                style="max-height: 300px; overflow: auto; white-space: pre-wrap; word-break: break-word;"
              >{{ draft.promptContent || '' }}</pre>
            </div>

            <div v-if="errorMessage" class="alert alert-danger" role="alert">
              {{ errorMessage }}
            </div>
          </template>
        </div>
        <div class="modal-footer" v-if="draft && !saving">
          <button type="button" class="btn btn-secondary" @click="onEdit">Go back</button>
          <button type="button" class="btn btn-primary" @click="onSubmit" :disabled="saving">
            Submit
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as Diff from 'diff';
import InsightsService from '../services/InsightsService.js';

function semverIncrementPatch(versionStr) {
  if (!versionStr || typeof versionStr !== 'string') return '1.0.0';
  const parts = versionStr.trim().split('.').map(s => parseInt(s, 10) || 0);
  if (parts.length === 0) return '1.0.0';
  parts[parts.length - 1]++;
  return parts.join('.');
}

function semverCompare(a, b) {
  const pa = (a || '').toString().split('.').map(Number);
  const pb = (b || '').toString().split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const na = pa[i] || 0;
    const nb = pb[i] || 0;
    if (na > nb) return 1;
    if (na < nb) return -1;
  }
  return 0;
}

export default {
  name: 'ReviewOrganizationPromptModal',
  props: {
    show: Boolean,
    draft: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      saving: false,
      errorMessage: '',
      nextVersion: null,
      loadingVersion: false,
      insightsService: new InsightsService(),
      model: streetmetrics.model,
      modalElement: null,
      currentModal: null,
      _hiddenModalHandler: null,
      _closedFromSubmit: false
    };
  },
  computed: {
    diffSegments() {
      if (!this.draft) return [];
      const oldText = this.draft.previousContent != null ? String(this.draft.previousContent) : '';
      const newText = this.draft.promptContent != null ? String(this.draft.promptContent) : '';
      return Diff.diffWords(oldText, newText);
    }
  },
  watch: {
    show(val) {
      if (val) {
        this.openModal();
        this.loadNextVersion();
      } else {
        this.closeModal();
        this.nextVersion = null;
        this.errorMessage = '';
      }
    }
  },
  mounted() {
    this.modalElement = document.getElementById('reviewOrganizationPromptModal');
    if (this.modalElement) {
      this.currentModal = new bootstrap.Modal(this.modalElement);
      this._hiddenModalHandler = () => {
        const backdrop = document.querySelector('.modal-backdrop');
        if (backdrop) backdrop.remove();
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        if (!this._closedFromSubmit) {
          this.$emit('edit');
        }
        this._closedFromSubmit = false;
      };
      this.modalElement.addEventListener('hidden.bs.modal', this._hiddenModalHandler);
      if (this.show) {
        this.openModal();
        this.loadNextVersion();
      }
    }
  },
  beforeDestroy() {
    if (this.modalElement && this._hiddenModalHandler) {
      this.modalElement.removeEventListener('hidden.bs.modal', this._hiddenModalHandler);
    }
  },
  methods: {
    onEdit() {
      this.closeModal();
    },
    async loadNextVersion() {
      if (!this.draft || !this.draft.customerId) {
        this.nextVersion = '1.0.0';
        return;
      }
      this.loadingVersion = true;
      this.nextVersion = null;
      try {
        const history = await this.insightsService.getOrganizationPromptHistory(
          parseInt(this.draft.customerId, 10)
        );
        const list = history || [];
        if (list.length === 0) {
          this.nextVersion = '1.0.0';
        } else {
          const sorted = [...list].sort((a, b) => semverCompare(b.version, a.version));
          const latest = sorted[0].version;
          this.nextVersion = semverIncrementPatch(latest);
        }
      } catch (err) {
        console.error('Error loading version:', err);
        this.nextVersion = '—';
      } finally {
        this.loadingVersion = false;
      }
    },
    openModal() {
      if (this.currentModal) {
        this.currentModal.show();
      }
    },
    closeModal() {
      if (this.currentModal) {
        this.currentModal.hide();
      }
    },
    async onSubmit() {
      if (!this.draft || !this.draft.customerId) return;

      this.saving = true;
      this.errorMessage = '';

      try {
        await this.insightsService.createOrganizationPrompt(
          parseInt(this.draft.customerId, 10),
          (this.draft.promptContent || '').trim()
        );

        this.model.toast.message = 'Organization prompt created successfully';
        this.model.toast.background = 'bg-success';

        this._closedFromSubmit = true;
        this.closeModal();
        this.$emit('created', parseInt(this.draft.customerId, 10));
      } catch (error) {
        console.error('Error creating organization prompt:', error);
        this.errorMessage =
          error.response?.data?.message || 'Error creating organization prompt. Please try again.';
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.form-label.text-muted {
  margin-bottom: 0.25rem;
}

.prompt-diff {
  line-height: 1.6;
  font-family: inherit;
}

.prompt-diff .diff-removed {
  background-color: #ffd7d5;
  color: #cf222e;
  text-decoration: line-through;
  padding: 0 1px;
  border-radius: 2px;
}

.prompt-diff .diff-added {
  background-color: #dafbe1;
  color: #1a7f37;
  padding: 0 1px;
  border-radius: 2px;
}
</style>
